import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ConnectedPosition } from '@angular/cdk/overlay';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { YeehroCalendlyAvailabilityService, YeehroCalendlyBookingService } from '@app/services';

interface SlotItem {
  time: string;
  startLabel: string;
  endLabel: string;
  url: string;
}

interface DaySlots {
  dateLabel: string;
  dateStr: string;
  allSlots: Array<{ time: string }>;
  selectedTimes: Set<string>;
  visibleSlots: SlotItem[];
}

// Phase 1 of "Offer time slots in email" — Calendly-style flow that lets the
// recruiter pick a date range (next 3 / next 5 / this week / next week),
// preview the slot grid with per-date edit + remove controls, and copy the
// result to the clipboard as both plain text and HTML so it pastes correctly
// into Gmail (rich) and bare text editors. Each slot is a clickable link to
// the public booking page with the date+time encoded in the URL.
//
// Inputs (set by caller via dialogRef.componentInstance):
//   event         — the YeehroCalendlyEvent to share slots for. Required.
//   userSettings  — host's user settings (must have bookingUsername).
//
// No timezone switcher here — event's schedule timezone is read-only display;
// invitees switch timezone in the booking flow itself.
@Component({
  selector: 'offer-time-slots-dialog',
  templateUrl: './offer-time-slots-dialog.component.html',
  styleUrls: ['./offer-time-slots-dialog.component.scss'],
  standalone: false
})
export class OfferTimeSlotsDialogComponent implements OnInit {
  event: any = null;
  userSettings: any = null;

  loading: boolean = true;
  copied: boolean = false;
  errorMessage: string = '';

  // Default selection size — Calendly defaults to showing the first 6 slots
  // per day, with edit to expand.
  private readonly DEFAULT_SLOTS_PER_DAY = 6;

  rangeOptions = [
    { key: 'next-3', label: 'Next 3 available days' },
    { key: 'next-5', label: 'Next 5 available days' },
    { key: 'this-week', label: 'This week' },
    { key: 'next-week', label: 'Next week' }
  ];
  selectedRange: string = 'next-3';

  groupedSlots: DaySlots[] = [];

  bookingBaseUrl: string = '';
  bookingUsername: string = '';
  eventTimezone: string = '';
  eventTimezoneLabel: string = '';

  // Edit popover state — single popover at a time. `editingSelection` is a
  // working copy so Cancel can discard without touching the underlying day.
  editingDateStr: string = '';
  editingSelection: Set<string> = new Set();

  // CDK Overlay anchor preferences — primary is below-right of the edit icon
  // (mirrors Calendly). Fallback flips above when there's no room.
  overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 }
  ];

  private _copiedTimer: any = null;
  private _rawDays: Array<{ date: string; slots: Array<{ time: string }> }> = [];


  constructor(
    private _dialogRef: MatDialogRef<OfferTimeSlotsDialogComponent>,
    private _bookingService: YeehroCalendlyBookingService,
    private _availabilityService: YeehroCalendlyAvailabilityService
  ) {}


  async ngOnInit() {
    this.bookingBaseUrl = 'https://yeehro.com/booking/';
    this.bookingUsername = (this.userSettings?.bookingUsername || '').trim();

    if (!this.event?._id) {
      this.errorMessage = 'No event selected.';
      this.loading = false;
      return;
    }
    if (!this.bookingUsername) {
      this.errorMessage = 'Your booking username is not set — set it on the Calendly user settings page first.';
      this.loading = false;
      return;
    }

    this.eventTimezone = this.event?.schedule?.timezone || 'America/New_York';
    this.eventTimezoneLabel = this.formatTimezoneLabel(this.eventTimezone);

    await this.loadSlots();
  }


  // Fetch current + next month so range queries crossing month boundaries
  // still have data. Server filters by min-notice + buffer + bookings.
  async loadSlots() {
    this.loading = true;
    this.errorMessage = '';
    try {
      const now = new Date();
      const monthsToFetch = this.getMonthKeysToFetch(now);
      // Host = the sender (a public template resolves to their schedule, matching
      // the per-slot URLs). Only active meeting types are offered — inactive ones
      // aren't bookable, so their links wouldn't resolve.
      const responses = await Promise.all(
        monthsToFetch.map((m: string) => lastValueFrom(this._bookingService.getAvailableSlots(this.event._id, m, this.bookingUsername)))
      );

      const dayMap = new Map<string, Array<{ time: string }>>();
      let scheduleTz: string = '';
      for (const resp of responses) {
        if (resp?.timezone && !scheduleTz) scheduleTz = resp.timezone;
        for (const day of (resp?.days || [])) {
          if (!dayMap.has(day?.date)) dayMap.set(day.date, day?.slots || []);
        }
      }
      if (scheduleTz) {
        this.eventTimezone = scheduleTz;
        this.eventTimezoneLabel = this.formatTimezoneLabel(scheduleTz);
      }
      this._rawDays = Array.from(dayMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, slots]) => ({ date, slots }));

      this.applyRangeFilter();
    } catch (err) {
      console.log('Error loading offer-time-slots: ', err);
      this.errorMessage = String(err || '').toLowerCase().includes('inactive')
        ? 'This meeting type isn’t active — turn it on to offer its times.'
        : 'Unable to load available times. Please try again.';
      this._rawDays = [];
      this.groupedSlots = [];
    } finally {
      this.loading = false;
    }
  }


  onRangeChange(key: string) {
    if (!key || key === this.selectedRange) return;
    this.selectedRange = key;
    this.closeEdit();
    this.applyRangeFilter();
  }


  // Narrow _rawDays by selectedRange and build the DaySlots view models.
  // Default selection is the first DEFAULT_SLOTS_PER_DAY entries — recruiter
  // can expand via the per-row edit popover.
  private applyRangeFilter() {
    const today = this.todayInEventTz();
    let filtered: typeof this._rawDays = [];

    if (this.selectedRange === 'next-3' || this.selectedRange === 'next-5') {
      const count = this.selectedRange === 'next-3' ? 3 : 5;
      filtered = this._rawDays.filter(d => d.date >= today).slice(0, count);
    } else if (this.selectedRange === 'this-week') {
      const endOfWeek = this.endOfWeek(today);
      filtered = this._rawDays.filter(d => d.date >= today && d.date <= endOfWeek);
    } else if (this.selectedRange === 'next-week') {
      const nextMon = this.addDays(this.endOfWeek(today), 1);
      const nextSun = this.addDays(nextMon, 6);
      filtered = this._rawDays.filter(d => d.date >= nextMon && d.date <= nextSun);
    }

    this.groupedSlots = filtered.map(day => {
      const defaultSelected = (day.slots || []).slice(0, this.DEFAULT_SLOTS_PER_DAY).map(s => s.time);
      const ds: DaySlots = {
        dateLabel: this.formatDayLabel(day.date),
        dateStr: day.date,
        allSlots: day.slots || [],
        selectedTimes: new Set(defaultSelected),
        visibleSlots: []
      };
      this.recomputeVisibleSlots(ds);
      return ds;
    });
  }


  // Rebuild a day's visibleSlots array from its selectedTimes — called any
  // time the selection changes (Done in edit popover).
  private recomputeVisibleSlots(day: DaySlots) {
    const duration = Number(this.event?.duration) || 30;
    day.visibleSlots = (day.allSlots || [])
      .filter(s => day.selectedTimes.has(s.time))
      .map(s => this.buildSlotItem(day.dateStr, s.time, duration));
  }


  private buildSlotItem(dateStr: string, time: string, duration: number): SlotItem {
    const slug = this.event?.bookingLinkId || this.event?._id;
    const startLabel = time || '';
    const endLabel = this.addMinutesToTimeStr(time, duration);
    const hhmm = this.timeStrToHHmm(time);
    const url = `${this.bookingBaseUrl}${this.bookingUsername}/${slug}/${dateStr}-${hhmm}`;
    return { time, startLabel, endLabel, url };
  }


  // ── Per-date edit popover ─────────────────────────────


  onOpenEdit(day: DaySlots, e?: Event) {
    e?.stopPropagation();
    this.editingDateStr = day.dateStr;
    this.editingSelection = new Set(day.selectedTimes);
  }


  closeEdit() {
    this.editingDateStr = '';
    this.editingSelection = new Set();
  }


  isEditingSelected(time: string): boolean {
    return this.editingSelection.has(time);
  }


  toggleEditingSlot(time: string) {
    if (this.editingSelection.has(time)) this.editingSelection.delete(time);
    else this.editingSelection.add(time);
  }


  selectAllInEdit(day: DaySlots) {
    this.editingSelection = new Set((day.allSlots || []).map(s => s.time));
  }


  clearAllInEdit() {
    this.editingSelection = new Set();
  }


  // Done disabled rule: if this is the only date left and the working set is
  // empty, prevent confirm (would remove the last date — not allowed).
  canConfirmEdit(): boolean {
    if (this.editingSelection.size > 0) return true;
    return this.groupedSlots.length > 1;
  }


  // Commit the working set. Empty selection on a non-last date means the user
  // is using "clear all → Done" to remove the date — mirror the trash button.
  confirmEdit(day: DaySlots) {
    if (this.editingSelection.size === 0) {
      if (this.groupedSlots.length <= 1) return;
      this.removeDate(day);
      this.closeEdit();
      return;
    }
    day.selectedTimes = new Set(this.editingSelection);
    this.recomputeVisibleSlots(day);
    this.closeEdit();
  }


  onRemoveDate(day: DaySlots, e?: Event) {
    e?.stopPropagation();
    if (this.groupedSlots.length <= 1) return; // never remove the last date
    this.removeDate(day);
  }


  private removeDate(day: DaySlots) {
    this.groupedSlots = this.groupedSlots.filter(d => d.dateStr !== day.dateStr);
    if (this.editingDateStr === day.dateStr) this.closeEdit();
  }


  // ── Copy to clipboard ──────────────────────────────────
  async onCopy() {
    if (!this.groupedSlots?.length) return;
    const plain = this.buildPlainText();
    const html = this.buildHtml();
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator?.clipboard?.write) {
        const item = new ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' })
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard?.writeText(plain);
      }

      clearTimeout(this._copiedTimer);
      this.copied = true;
      this._copiedTimer = setTimeout(() => this.copied = false, 2500);
    } catch (err) {
      console.log('Clipboard write failed: ', err);
      this.errorMessage = 'Unable to copy to clipboard. Your browser may have blocked it.';
    }
  }


  // Plain-text fallback used when the destination doesn't render HTML.
  // Mirrors the HTML structure: event header → per-day sections → see-all
  // link → powered-by footer. Each slot row keeps the URL inline so plain
  // mail clients can still resolve links.
  private buildPlainText(): string {
    const lines: string[] = [];
    const eventName = (this.event?.name || '').trim();
    const duration = Number(this.event?.duration) || 0;
    if (eventName) lines.push(eventName);
    const meta: string[] = [];
    if (duration) meta.push(`${duration} min`);
    if (this.eventTimezoneLabel) meta.push(`Time zone: ${this.eventTimezoneLabel}`);
    if (meta.length) lines.push(meta.join(' · '));
    lines.push('');

    for (const day of this.groupedSlots) {
      if (!day.visibleSlots.length) continue;
      lines.push(day.dateLabel);
      for (const slot of day.visibleSlots) {
        lines.push(`  ${slot.startLabel}  ${slot.url}`);
      }
      lines.push('');
    }
    const landing = `${this.bookingBaseUrl}${this.bookingUsername}/${this.event?.bookingLinkId || this.event?._id}`;
    lines.push(`See all available times: ${landing}`);
    lines.push('');
    lines.push('powered by Yeehro');
    return lines.join('\n');
  }


  // Rich HTML used when pasting into Gmail / Outlook / any rich-text composer.
  // Inline styles only (email clients strip <style>), inline-block + margin
  // (no flex — Gmail strips), and uniform pill styling for each time chip.
  // Structure matches Calendly's pasted output: event header, per-day pill
  // grids, see-all link, powered-by footer.
  private buildHtml(): string {
    const safeName = this.escapeHtml(this.event?.name || '');
    const duration = Number(this.event?.duration) || 0;
    const safeDuration = duration ? `${duration} min` : '';
    const safeTz = this.escapeHtml(this.eventTimezoneLabel);
    const landing = `${this.bookingBaseUrl}${this.bookingUsername}/${this.event?.bookingLinkId || this.event?._id}`;
    const safeLanding = this.escapeHtml(landing);

    const chipStyle = [
      'display:inline-block',
      'padding:7px 14px',
      'margin:0 6px 6px 0',
      'border:1px solid #b8c4d6',
      'border-radius:4px',
      'background:#ffffff',
      'color:#004eba',
      'font-size:13px',
      'font-weight:500',
      'text-decoration:none',
      'line-height:1',
      'vertical-align:middle'
    ].join(';');

    const sections = this.groupedSlots
      .filter(d => d.visibleSlots.length)
      .map(day => {
        const safeDate = this.escapeHtml(day.dateLabel);
        const chips = day.visibleSlots.map(slot => {
          const label = this.escapeHtml(slot.startLabel);
          const href = this.escapeHtml(slot.url);
          return `<a href="${href}" style="${chipStyle}">${label}</a>`;
        }).join('');
        return `<div style="margin-top:18px;">`
          + `<div style="font-weight:700; color:#1a1a2e; font-size:14px; margin-bottom:10px;">${safeDate}</div>`
          + `<div>${chips}</div>`
          + `</div>`;
      }).join('');

    const headerLines: string[] = [];
    if (safeName) headerLines.push(`<div style="font-weight:700; font-size:16px; color:#1a1a2e;">${safeName}</div>`);
    if (safeDuration) headerLines.push(`<div style="color:#4a5261; margin-top:2px;">${safeDuration}</div>`);
    if (safeTz) headerLines.push(`<div style="color:#4a5261; margin-top:2px;">Time zone: ${safeTz}</div>`);

    return `<div style="font-family: Arial, Helvetica, sans-serif; color:#1a1a2e; font-size:14px; line-height:1.4;">`
      + headerLines.join('')
      + sections
      + `<div style="margin-top:20px;"><a href="${safeLanding}" style="color:#004eba; text-decoration:underline; font-size:13px;">See all available times</a></div>`
      + `<div style="margin-top:14px; padding-top:10px; border-top:1px solid #e6ecf5; color:#8a94a6; font-size:12px;">powered by <span style="color:#004eba; font-weight:500;">Yeehro</span></div>`
      + `</div>`;
  }


  private escapeHtml(value: string): string {
    if (!value) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }


  // ── Date/time helpers ─────────────────────


  private todayInEventTz(): string {
    return this.dateStringInTz(new Date(), this.eventTimezone);
  }


  private dateStringInTz(d: Date, tz: string): string {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    } catch (err) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  }


  // YYYY-MM-DD of the upcoming Sunday (or today if today is Sunday) — week closer.
  private endOfWeek(dateStr: string): string {
    const d = this.parseDateStr(dateStr);
    const dow = d.getDay();
    const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
    return this.dateStringFromDate(this.addDaysToDate(d, daysUntilSunday));
  }


  private addDays(dateStr: string, days: number): string {
    const d = this.parseDateStr(dateStr);
    return this.dateStringFromDate(this.addDaysToDate(d, days));
  }


  private addDaysToDate(d: Date, days: number): Date {
    const copy = new Date(d.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
  }


  private parseDateStr(dateStr: string): Date {
    const [y, m, dd] = (dateStr || '').split('-').map(Number);
    return new Date(y || 1970, (m || 1) - 1, dd || 1);
  }


  private dateStringFromDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }


  private getMonthKeysToFetch(now: Date): string[] {
    const monthOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const current = monthOf(now);
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return [current, monthOf(next)];
  }


  private formatDayLabel(dateStr: string): string {
    const d = this.parseDateStr(dateStr);
    try {
      return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(d);
    } catch (err) {
      return dateStr;
    }
  }


  // "9:00am" → "0900". Accepts am/pm and 24h. Returns '' on malformed.
  private timeStrToHHmm(time: string): string {
    if (!time) return '';
    const m = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!m) {
      const m24 = time.match(/^(\d{1,2}):(\d{2})$/);
      if (m24) return `${String(parseInt(m24[1])).padStart(2, '0')}${m24[2]}`;
      return '';
    }
    let h = parseInt(m[1]);
    const min = m[2];
    const ampm = m[3].toLowerCase();
    if (ampm === 'pm' && h !== 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}${min}`;
  }


  // Add `mins` to "9:00am"-style string → same format. Used for the end label.
  private addMinutesToTimeStr(time: string, mins: number): string {
    if (!time) return '';
    const m = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!m) return time;
    let h = parseInt(m[1]);
    const sm = parseInt(m[2]);
    const ampm = m[3].toLowerCase();
    if (ampm === 'pm' && h !== 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    const total = h * 60 + sm + (mins || 0);
    const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
    const eh24 = Math.floor(wrapped / 60);
    const em = wrapped % 60;
    const eampm = eh24 >= 12 ? 'pm' : 'am';
    const eh12 = eh24 % 12 || 12;
    return `${eh12}:${String(em).padStart(2, '0')}${eampm}`;
  }


  private formatTimezoneLabel(tz: string): string {
    if (!tz) return '';
    return this._availabilityService?.getTimezoneLabel(tz) || tz.replace(/_/g, ' ');
  }


  close() {
    this._dialogRef.close(null);
  }
}
